import { GenericHttpError } from '@iconik-io/core/src/http/generic_http_transport';
import { isString } from '@iconik-io/core/src/is_string';
import retryWithExponentialBackoff from '@iconik-io/core/src/retry_with_backoff';
import type { NonNullableBy, RequiredBy } from '@iconik-io/core/src/types';
import { AbortEndpointRequestError } from '@iconik-io/iconik-api/src/client/response';
import {
  type FilesMultipartB2StartUpload,
  httpGetAssetsByAssetIdFilesByFileId,
} from '@iconik-io/iconik-api/src/services/files';
import { unwrapResponseBody } from '@iconik-io/iconik-api/src/utils/unwrap_response_body';

import type { GenericRetryOptions } from '../../client/request';
import {
  AbortUploadError,
  CancelUploadError,
  FileSizeError,
  InvalidFileObject,
  InvalidUploadObjectError,
  MultipartUploadChunkError,
  UninitializedChunkMetaError,
} from '../../errors';
import {
  UPLOADER_CHUNK_STATUS_COMPLETED,
  UPLOADER_CHUNK_STATUS_FAILED,
  UPLOADER_CHUNK_STATUS_UPLOADING,
  UPLOADER_STATUS_COMPLETING,
} from '../../state';
import { unwrapFileUploadKind } from '../../state/upload_kind';
import type { UploadEntity } from '../../types';
import { fitChunkSize } from '../../utils/fit_chunk_size';
import { sha1 } from '../../utils/sha1';
import { MultipartUploader } from '../multipart_uploader';
import {
  B2EmptyChunkHashError,
  B2NonMultiMetaError,
  B2NonSingleMetaError,
  B2UnsupportedError,
} from './errors';

const FILE_SIZE_LIMIT = 10_000_000_000_000;

const MAX_B2_CHUNKS_NUMBER = 10_000;

const B2_RETRY_OPTIONS: GenericRetryOptions = {
  retries: 10,
  minTimeout: 200,
  maxTimeout: 30_000,
  // Don't retry 50X errors here. They need special handling in the B2 case. See code in #uploadChunkWith50XHandling
  status: [400, 408, 429],
  method: ['post'],
};

type UploadObject = NonNullableBy<
  RequiredBy<
    FilesMultipartB2StartUpload,
    'authorization_token' | 'upload_file_id' | 'upload_url'
  >,
  'upload_file_id'
>;

type B2MetaSingle = {
  type: 'single';
  uploadUrl: string;
  uploadFilename: string;
  authorizationToken: string;
};

type B2MetaMulti = {
  type: 'multi';
  uploadFileId: string;
};

type B2Meta = B2MetaSingle | B2MetaMulti;

type B2ChunkMeta = {
  upload: UploadObject;
  hash?: string;
};

export class B2Uploader extends MultipartUploader<B2Meta, B2ChunkMeta> {
  protected validate(): boolean {
    if (this.state.fileInfo.size > FILE_SIZE_LIMIT) {
      throw new FileSizeError(FILE_SIZE_LIMIT);
    }
    // Single chunk uploads go straight to the URL on the entity and need no
    // native B2 endpoints.
    if (!this.#isSingleChunkUpload() && !this.endpoints.b2) {
      throw new B2UnsupportedError();
    }
    return true;
  }

  #unwrapB2Endpoints() {
    const b2Endpoints = this.endpoints.b2;
    if (!b2Endpoints) {
      throw new B2UnsupportedError();
    }
    return b2Endpoints;
  }

  #handleUploadEntityUpdated({ entity }: { entity: UploadEntity }) {
    if (!this.#isSingleChunkUpload()) {
      return;
    }
    if (!entity.upload_url) {
      throw new InvalidFileObject(entity, 'Upload url is not set');
    }
    if (!entity.upload_filename) {
      throw new InvalidFileObject(entity, 'Upload filename is not set');
    }
    const authorizationToken = entity.upload_credentials?.authorizationToken;
    if (!authorizationToken) {
      throw new InvalidFileObject(entity, 'Authorization token is not set');
    }
    if (!isString(authorizationToken)) {
      throw new InvalidFileObject(entity, 'Authorization token is not string');
    }
    this.updateState({
      meta: {
        type: 'single',
        uploadUrl: entity.upload_url,
        uploadFilename: entity.upload_filename,
        authorizationToken,
      },
    });
  }

  init() {
    this.on('uploadEntityCreated', this.#handleUploadEntityUpdated);
    super.init();
  }

  #unwrapSingleMeta(): B2MetaSingle {
    const meta = this.unwrapMeta();
    if (meta.type !== 'single') {
      throw new B2NonSingleMetaError(
        'Non-single meta type is used in single chunk upload context',
      );
    }
    return meta;
  }

  #unwrapMultiMeta(): B2MetaMulti {
    const meta = this.unwrapMeta();
    if (meta.type !== 'multi') {
      throw new B2NonMultiMetaError(
        'Non-multi meta type is used in multi chunk upload context',
      );
    }
    return meta;
  }

  async #runSingleChunkUpload(): Promise<void> {
    const meta = this.#unwrapSingleMeta();
    const chunkIndex = 0;
    this.updateChunks([
      { index: chunkIndex, status: UPLOADER_CHUNK_STATUS_UPLOADING },
    ]);

    const data = await this.readChunkData(0, this.abortList.newSignal());
    const hash = await sha1(data);
    await this.httpClient.request(
      {
        method: 'post',
        url: meta.uploadUrl,
        headers: {
          Authorization: meta.authorizationToken,
          'Content-Type': this.state.fileInfo.mimeType,
          'X-Bz-File-Name': encodeURIComponent(meta.uploadFilename),
          'X-Bz-Content-Sha1': hash,
        },
        body: data,
      },
      {
        onUploadProgress: (event) => this.uploadChunkProgress(0, event),
        abortSignal: this.abortList.newSignal(),
        retryOptions: B2_RETRY_OPTIONS,
      },
    );

    this.updateChunks([
      { index: chunkIndex, status: UPLOADER_CHUNK_STATUS_COMPLETED },
    ]);
  }

  #uploadChunkIndex = async (chunkIndex: number): Promise<void> => {
    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();

    const uploadObject = await unwrapResponseBody(
      this.apiClient.request(
        this.#unwrapB2Endpoints().start(assetId, entityId, {
          upload_file_id: this.#unwrapMultiMeta().uploadFileId,
        }),
        {
          abortSignal: this.abortList.newSignal(),
          retryOptions: {
            ...B2_RETRY_OPTIONS,
            shouldRetry: (response) => {
              if (response.status === 200) {
                // B2 might return an empty upload url even though we get a 200 back so check that
                // on the response
                this.#assertUploadObject(response.body);
              }
              return false;
            },
          },
        },
      ),
    );

    this.#assertUploadObject(uploadObject);

    this.updateChunks([
      {
        index: chunkIndex,
        meta: { upload: uploadObject },
        status: UPLOADER_CHUNK_STATUS_UPLOADING,
      },
    ]);

    const body = await this.readChunkData(
      chunkIndex,
      this.abortList.newSignal(),
    );
    const hash = await sha1(body);

    const chunkMeta = this.unwrapChunkMeta(chunkIndex);
    this.updateChunks([
      {
        index: chunkIndex,
        meta: { ...chunkMeta, hash },
      },
    ]);

    return this.httpClient
      .request(
        {
          method: 'post',
          url: chunkMeta.upload.upload_url,
          body,
          headers: {
            Authorization: chunkMeta.upload.authorization_token,
            'X-Bz-Part-Number': String(chunkIndex + 1),
            'X-Bz-Content-Sha1': hash,
          },
        },
        {
          onUploadProgress: (event) =>
            this.uploadChunkProgress(chunkIndex, event),
          abortSignal: this.abortList.newSignal(),
          retryOptions: B2_RETRY_OPTIONS,
        },
      )
      .then(() => {
        this.updateChunks([
          {
            index: chunkIndex,
            status: UPLOADER_CHUNK_STATUS_COMPLETED,
          },
        ]);
      });
  };

  // B2 has special handing of 50X errors. If we get one of those we should fetch a new upload URL.
  // Thats what this special retry-logic is about.
  // See docs here: https://www.backblaze.com/docs/cloud-storage-upload-files-with-the-native-api
  #uploadChunkWith50XHandling = async (chunkIndex: number): Promise<void> =>
    retryWithExponentialBackoff(() => this.#uploadChunkIndex(chunkIndex), {
      shouldRetry: ({ result }) => {
        if (result.ok) {
          return false;
        }
        const error = result.error;

        if (
          error instanceof GenericHttpError &&
          error.response.status >= 500 &&
          error.response.status < 510
        ) {
          this.emit('log', {
            message: `Got ${error.response.status} from B2. Retrying chunk ${chunkIndex} with new URL`,
            level: 'info',
            state: this.state,
          });
          return true;
        }
        return false;
      },
      // In this case where we get a 50X we dont need to wait. Just fetch an updated upload URL
      calculateDelay: () => 1,
      maxRetries: 10,
      abortSignal: this.abortList.newSignal(),
    });

  #assertUploadObject(
    urlObject: FilesMultipartB2StartUpload,
  ): asserts urlObject is UploadObject {
    if (!urlObject.authorization_token) {
      throw new InvalidUploadObjectError(
        urlObject,
        'Authorization token is not set',
      );
    }
    if (!urlObject.upload_url) {
      throw new InvalidUploadObjectError(urlObject, 'Upload URL is not set');
    }
    if (!urlObject.upload_file_id) {
      throw new InvalidUploadObjectError(
        urlObject,
        'Upload file ID is not set',
      );
    }
  }

  async #fetchMultipartMeta(): Promise<B2MetaMulti> {
    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();

    const abortSignal = this.abortList.newSignal();

    return unwrapResponseBody(
      this.apiClient.request(
        this.#unwrapB2Endpoints().start(assetId, entityId, {}),
        {
          abortSignal,
          retryOptions: {
            ...B2_RETRY_OPTIONS,
            shouldRetry: (response) => {
              if (response.status === 200) {
                // B2 might return an empty upload url even though we get a 200 back so check that
                // on the response
                this.#assertUploadObject(response.body);
              }
              return false;
            },
          },
        },
      ),
    ).then((response) => {
      this.#assertUploadObject(response);
      return { type: 'multi', uploadFileId: response.upload_file_id };
    });
  }

  async #runMultiChunkUpload(): Promise<void> {
    await this.chunksQueue.addAll(
      this.getIncompleteChunks().map(
        (chunk) => () =>
          this.#uploadChunkWith50XHandling(chunk.index).catch((error) => {
            if (
              error instanceof AbortUploadError ||
              error instanceof AbortEndpointRequestError
            ) {
              throw error;
            }
            this.updateChunks([
              {
                index: chunk.index,
                status: UPLOADER_CHUNK_STATUS_FAILED,
              },
            ]);
            throw new MultipartUploadChunkError(chunk, error);
          }),
      ),
    );
  }

  checkIfUploadExpired(_assetId: string, _fileId: string): Promise<boolean> {
    return Promise.resolve(false);
  }

  protected async recover(): Promise<void> {
    await super.recover();

    if (!this.#isSingleChunkUpload()) {
      return;
    }

    // For single chunk uploads we use the file object to get the upload URL and authorization token
    // So we need to fetch a new file object each time we retry a failed upload since the upload URL
    // might have expired.
    const assetId = this.state.target.assetId;
    const fileId = unwrapFileUploadKind(this.state).fileId;
    if (!assetId || !fileId) {
      return;
    }

    this.emit('log', {
      level: 'debug',
      message: 'Recovering single chunk upload. Fetching new file object.',
      state: this.state,
    });

    const file = await unwrapResponseBody(
      this.apiClient.request(
        httpGetAssetsByAssetIdFilesByFileId(assetId, fileId, {
          generate_signed_post_url: true,
        }),
        { abortSignal: this.abortList.newSignal() },
      ),
    );
    this.#handleUploadEntityUpdated({ entity: file });
  }

  protected async runUpload(): Promise<void> {
    if (this.#isSingleChunkUpload()) {
      await this.#runSingleChunkUpload();
      return;
    }

    // First run so fetch meta object to get B2 file id for upload
    if (!this.state.meta) {
      const meta = await this.#fetchMultipartMeta();
      this.updateState({ meta });
    }

    await this.#runMultiChunkUpload();
    await this.#finishMultiChunk();
  }

  #isSingleChunkUpload() {
    return this.unwrapChunks().length === 1;
  }

  async #finishMultiChunk(): Promise<void> {
    this.updateState({ status: UPLOADER_STATUS_COMPLETING });

    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();
    const sha1List: string[] = [];
    for (const chunk of this.unwrapChunks()) {
      if (!chunk.meta) {
        throw new UninitializedChunkMetaError(chunk);
      }
      if (!chunk.meta.hash) {
        throw new B2EmptyChunkHashError(chunk);
      }
      sha1List.push(chunk.meta.hash);
    }

    const abortSignal = this.abortList.newSignal();

    await this.apiClient.request(
      this.#unwrapB2Endpoints().finish(assetId, entityId, {
        sha1_list: sha1List,
        upload_file_id: this.#unwrapMultiMeta().uploadFileId,
      }),
      { abortSignal, retryOptions: B2_RETRY_OPTIONS },
    );
  }

  protected getChunkSize() {
    return fitChunkSize(
      this.state.fileInfo.size,
      super.getChunkSize(),
      MAX_B2_CHUNKS_NUMBER,
    );
  }

  protected async runCancel(): Promise<void> {
    if (!this.#isSingleChunkUpload()) {
      const assetId = this.state.target.assetId;
      const fileId = unwrapFileUploadKind(this.state).fileId;
      if (!assetId || !fileId) {
        return;
      }
      let uploadFileId: string;
      try {
        uploadFileId = this.#unwrapMultiMeta().uploadFileId;
      } catch {
        // We never managed to get a upload file id from B2 so just bail out here
        return;
      }

      await this.apiClient
        .request(
          this.#unwrapB2Endpoints().cancel(assetId, fileId, {
            upload_file_id: uploadFileId,
          }),
          // Don't use B2_RETRY_OPTIONS in this case since that retries on 400 and we
          // don't want that here. It will still use the default retry options defined in
          // the HTTP client so timeouts or other status codes will still retry.
          //
          // No abort signal either: cancelling aborts the abort list first, so a
          // signal from it would cancel this request the moment it is issued.
          {},
        )
        .catch((error) => {
          throw new CancelUploadError(error);
        });
    }
  }
}
